/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

/**
 * Azure WAF request-body inspection (rule 200002) requires the body to be valid JSON
 * when Content-Type is application/json. Raw GraphQL documents are not JSON.
 * REST payloads that are already JSON are left unchanged.
 */
export function asApplicationJsonBody(body: any): any {
    if (body == null || typeof body !== 'string') {
        return body;
    }
    const trimmed = body.trim();
    if (!trimmed) {
        return body;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
        try {
            JSON.parse(trimmed);
            return body;
        } catch {
            // Not valid JSON — GraphQL selection sets can start with '{'.
        }
    }
    if (/^(query|mutation|subscription)\b/i.test(trimmed) || trimmed.startsWith('{')) {
        return JSON.stringify({ query: body });
    }
    return body;
}

export const CLOSED_SERVICE_CASE_CATEGORY_IDS = [1, 3];

export function isClosedServiceCaseCategory(id: number): boolean {
    return CLOSED_SERVICE_CASE_CATEGORY_IDS.indexOf(id) !== -1;
}

export function closedCaseDateVariables(startDate: Date, endDate: Date) {
    return {
        closedfromdate: startDate.toJSON(),
        closedtodate: endDate.toJSON()
    };
}

export function isAllServiceCaseCategory(id: number): boolean {
    return id === 5;
}

export function allCaseDateVariables(startDate: Date, endDate: Date) {
    return {
        updatedfromdate: startDate.toJSON(),
        updatedtodate: endDate.toJSON()
    };
}

export function usesServiceCaseDateVariables(id: number): boolean {
    return isClosedServiceCaseCategory(id) || isAllServiceCaseCategory(id);
}

export function toServiceCaseDateGraphqlBody(query: string, categoryId: number, startDate: Date, endDate: Date) {
    if (isClosedServiceCaseCategory(categoryId)) {
        return toGraphqlJsonBody(query, closedCaseDateVariables(startDate, endDate));
    }
    if (isAllServiceCaseCategory(categoryId)) {
        return toGraphqlJsonBody(query, allCaseDateVariables(startDate, endDate));
    }
    return asApplicationJsonBody(query);
}

export function toGraphqlJsonBody(query: string, variables?: Record<string, unknown>): any {
    if (variables && Object.keys(variables).length > 0) {
        return JSON.stringify({ query, variables });
    }
    return asApplicationJsonBody(query);
}

export interface GridGraphqlFilter {
    filterProp: string;
    filterText: any;
}

export interface GridGraphqlExtras {
    pageno?: number;
    pagesize?: number;
    orderby?: string;
    filters?: GridGraphqlFilter[];
}

const INT_GRID_ARGS: Record<string, boolean> = {
    id: true,
    age: true,
    status: true,
    pageno: true,
    pagesize: true,
    warrantylabour: true,
    billablelabour: true
};

const DATE_GRID_ARGS: Record<string, boolean> = {
    opendate: true,
    closeddate: true,
    closedfromdate: true,
    closedtodate: true,
    updatedfromdate: true,
    updatedtodate: true,
    updateddate: true,
    startdate: true,
    enddate: true
};

export function graphqlTypeForGridArg(name: string): string {
    if (INT_GRID_ARGS[name]) {
        return 'Int';
    }
    if (DATE_GRID_ARGS[name]) {
        return 'Date';
    }
    return 'String';
}

/** Search UI wraps GraphQL string/date literals in quotes (`"E040405"`). Variables need the raw value. */
export function unwrapQuotedGraphqlLiteral(filterText: any): unknown {
    if (typeof filterText !== 'string') {
        return filterText;
    }
    const trimmed = filterText.trim();
    if (trimmed.length >= 2 && trimmed.charAt(0) === '"' && trimmed.charAt(trimmed.length - 1) === '"') {
        try {
            return JSON.parse(trimmed);
        } catch {
            return trimmed.slice(1, -1);
        }
    }
    return filterText;
}

export function collectGridArgVariables(extras?: GridGraphqlExtras): {
    defs: string[];
    fieldArgs: string[];
    variables: Record<string, unknown>;
} {
    const defs: string[] = [];
    const fieldArgs: string[] = [];
    const variables: Record<string, unknown> = {};
    if (!extras) {
        return { defs, fieldArgs, variables };
    }

    const add = (name: string, value: unknown) => {
        if (value === undefined || value === null || value === '') {
            return;
        }
        if (Object.prototype.hasOwnProperty.call(variables, name)) {
            return;
        }
        const gqlType = graphqlTypeForGridArg(name);
        defs.push('$' + name + ': ' + gqlType);
        fieldArgs.push(name + ': $' + name);
        variables[name] = value;
    };

    add('orderby', extras.orderby);
    add('pageno', extras.pageno);
    add('pagesize', extras.pagesize);
    const filters = extras.filters || [];
    for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        if (!filter || !filter.filterProp) {
            continue;
        }
        add(filter.filterProp, unwrapQuotedGraphqlLiteral(filter.filterText));
    }
    return { defs, fieldArgs, variables };
}

/**
 * Move grid pagination / sort / column filters into GraphQL variables so quoted
 * values like serialnumber:"E040405" are not inspected as ARGS:query.
 *
 * Open/license templates: `field %s {` → `field(pageno: $pageno, serialnumber: $serialnumber) {`
 * Closed/All/training templates: `closedtodate: $closedtodate%s)` or `category: $category%s)` → extra args as `, orderby: $orderby, pageno: $pageno)`
 */
export function injectGridArgVariables(query: string, extras?: GridGraphqlExtras): {
    query: string;
    variables: Record<string, unknown>;
} {
    const collected = collectGridArgVariables(extras);
    let nextQuery = query;
    if (nextQuery.indexOf('%s') !== -1) {
        const insideExistingParens = /%s\)/.test(nextQuery) || /,\s*%s/.test(nextQuery);
        const replacement = collected.fieldArgs.length === 0
            ? ''
            : (insideExistingParens
                ? ', ' + collected.fieldArgs.join(', ')
                : '(' + collected.fieldArgs.join(', ') + ')');
        nextQuery = nextQuery.split('%s').join(replacement);
    }
    if (collected.defs.length > 0) {
        const defStr = collected.defs.join(', ');
        if (/^query\([^)]*\)\s*\{/.test(nextQuery)) {
            nextQuery = nextQuery.replace(/^query\(([^)]*)\)(\s*)\{/, (_match: string, existing: string, ws: string) => {
                return 'query(' + existing + ', ' + defStr + ')' + ws + '{';
            });
        } else if (nextQuery.indexOf('query{') === 0) {
            nextQuery = 'query(' + defStr + ')' + nextQuery.slice('query'.length);
        }
    }
    return { query: nextQuery, variables: collected.variables };
}

export function toGridGraphqlBody(
    queryTemplate: string,
    extras?: GridGraphqlExtras,
    additionalVariables?: Record<string, unknown>
): any {
    const injected = injectGridArgVariables(queryTemplate, extras);
    const merged: Record<string, unknown> = Object.assign({}, additionalVariables || {}, injected.variables);
    return toGraphqlJsonBody(injected.query, merged);
}

export function toServiceCaseGridGraphqlBody(
    queryTemplate: string,
    categoryId: number,
    startDate: Date | undefined,
    endDate: Date | undefined,
    extras?: GridGraphqlExtras
): any {
    let dateVars: Record<string, unknown> | undefined;
    if (usesServiceCaseDateVariables(categoryId) && startDate && endDate) {
        dateVars = isClosedServiceCaseCategory(categoryId)
            ? closedCaseDateVariables(startDate, endDate)
            : allCaseDateVariables(startDate, endDate);
    }
    return toGridGraphqlBody(queryTemplate, extras, dateVars);
}

/** Authorization tabs: 8 My Training, 9 Team Training, 10 Team Product, 11 All Training, 12 All Product. */
export function authorizationCategoryForId(id: number): string | undefined {
    switch (id) {
        case 8:
            return 'My';
        case 9:
        case 10:
            return 'Team';
        case 11:
        case 12:
            return 'All';
        default:
            return undefined;
    }
}

export function toAuthorizationGraphqlBody(query: string, categoryId: number, extras?: GridGraphqlExtras): any {
    const category = authorizationCategoryForId(categoryId);
    const additional = category ? { category } : undefined;
    if (extras || query.indexOf('%s') !== -1) {
        return toGridGraphqlBody(query, extras, additional);
    }
    if (query.indexOf('$category') === -1) {
        return asApplicationJsonBody(query);
    }
    return additional ? toGraphqlJsonBody(query, additional) : asApplicationJsonBody(query);
}

export function isGridGraphqlExtras(value: any): value is GridGraphqlExtras {
    return value != null && typeof value === 'object' && !Array.isArray(value) &&
        ('pageno' in value || 'pagesize' in value || 'orderby' in value || 'filters' in value);
}
